function noOp() { }
function union(setA, setB) {
    return new Set([...setA, ...setB]);
}
function intersection(setA, setB) {
    return new Set([...setA].filter(x => setB.has(x)));
}
function difference(setA, setB) {
    return new Set([...setA].filter(x => !setB.has(x)));
}
function isNaN(val) {
    if (typeof val === 'number') {
        return Number.isNaN(val);
    }
    return false;
}
function isNotDefined(val) {
    return val === undefined || val === null || isNaN(val);
}
function isDefined(val) {
    return !isNotDefined(val);
}
function isFalsy(val, override = []) {
    return isNotDefined(val) || val === false || override.includes(val);
}
function isTruthy(val, override = []) {
    return !isFalsy(val) || override.includes(val);
}
function isFalsyString(val) {
    return isFalsy(val, ['']);
}
function isTruthyString(val) {
    return !isFalsyString(val);
}
function resolve(variable, ...args) {
    if (typeof variable === 'function') {
        return variable(...args);
    }
    return variable;
}

function getContrastYIQ(color) {
    const r = parseInt(color.substr(1, 2), 16);
    const g = parseInt(color.substr(3, 2), 16);
    const b = parseInt(color.substr(5, 2), 16);
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / (255 * 1000);
    return yiq;
}
function isValidHexColor(color) {
    const colorHex = /(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)/i;
    return colorHex.test(color);
}
function getColorOnBgColor(color, colorOnLight = '#212121', colorOnDark = '#ffffff') {
    return getContrastYIQ(color) > 0.5 ? colorOnLight : colorOnDark;
}
function getHashFromString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i += 1) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return hash;
}
function getHexFromCode(code) {
    const color = (code & 0x00FFFFFF)
        .toString(16)
        .toUpperCase();
    const hex = '00000'.substring(0, 6 - color.length) + color;
    return `#${hex}`;
}
function getHexFromString(str) {
    return getHexFromCode(getHashFromString(str));
}
function getHexFromRgb(color) {
    const result = /^rgb\((\d+),\s*(\d+),\s*(\d+)/i.exec(color);
    if (!result) {
        return undefined;
    }
    const r = parseInt(result[1], 10);
    const g = parseInt(result[2], 10);
    const b = parseInt(result[3], 10);
    return getHexFromRgbRaw([r, g, b]);
}
function getHexFromRgbRaw(color) {
    const [r, g, b] = color;
    return `#${((r << 16) + (g << 8) + b).toString(16).padStart(6, '0')}`;
}
function getRgbRawFromHex(color) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
    if (!result) {
        return undefined;
    }
    return [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16),
    ];
}
function getRgbFromHex(hex) {
    const color = getRgbRawFromHex(hex);
    if (isNotDefined(color)) {
        return undefined;
    }
    const [r, g, b] = color;
    return `rgb(${r}, ${g}, ${b})`;
}
function interpolateRgb(color1, color2, factor = 0.5) {
    const result = [
        Math.round((factor * (color2[0] - color1[0])) + color1[0]),
        Math.round((factor * (color2[1] - color1[1])) + color1[1]),
        Math.round((factor * (color2[2] - color1[2])) + color1[2]),
    ];
    return result;
}

const comparision = (extractor, comparisionFunc) => (x, y, direction = 1) => {
    const a = isDefined(x) ? extractor(x) : undefined;
    const b = isDefined(y) ? extractor(y) : undefined;
    if (a === b) {
        return 0;
    }
    else if (isNotDefined(a)) {
        return direction * 1;
    }
    else if (isNotDefined(b)) {
        return direction * -1;
    }
    return direction * comparisionFunc(a, b);
};
const compareBoolean = comparision((x) => x, (a, b) => (Number(a) - Number(b)));
const compareString = comparision((x) => x, (a, b) => a.localeCompare(b));
const compareNumber = comparision((x) => x, (a, b) => (a - b));
const compareDate = comparision((x) => x, (a, b) => {
    const dateA = new Date(a);
    const dateB = new Date(b);
    return dateA.getTime() - dateB.getTime();
});
const compareStringAsNumber = comparision((x) => Number(x), (a, b) => a - b);
const compareLength = comparision((x) => x.length, (a, b) => (a - b));
const compareStringByWordCount = comparision((x) => x.split(/\s+/).length, (a, b) => a - b);
const compareStringSearch = (x, y, z, d) => {
    if (!z) {
        return 0;
    }
    return comparision((x) => x, (a, b) => {
        const searchText = z.toLowerCase();
        const firstIndex = a.toLowerCase().indexOf(searchText);
        const secondIndex = b.toLowerCase().indexOf(searchText);
        if (firstIndex === secondIndex) {
            return a.localeCompare(b);
        }
        if (firstIndex === -1 && secondIndex === -1) {
            return a.localeCompare(b);
        }
        else if (secondIndex === -1) {
            return -1;
        }
        else if (firstIndex === -1) {
            return 1;
        }
        return firstIndex - secondIndex;
    })(x, y, d);
};

function _cs(...props) {
    return props.filter(isTruthyString).join(' ');
}

function padStart(value, length, pad = '0') {
    if (isNotDefined(value)) {
        return '';
    }
    const str = String(value);
    return str.length >= length
        ? str
        : Array(length - str.length + 1).join(pad) + str;
}
function capitalize(str) {
    if (isTruthyString(str)) {
        return str.replace(/\b\w/g, l => l.toUpperCase());
    }
    return str;
}
function formatPdfText(text) {
    return text
        .replace(/\t/gi, ' ')
        .replace(/([^\n])[ \t]*\n[ \t]*(?!\n)/gi, '$1 ')
        .replace(/ +/gi, ' ')
        .replace(/\n\s*\n\s*(\n\s*)+/gi, '\n\n\n')
        .replace(/-\?/gi, '-')
        .trim();
}
function getRatingForContentInString(content, str) {
    if (isFalsyString(content) || isFalsyString(str)) {
        return -1;
    }
    return content.toLowerCase().indexOf(str.toLowerCase());
}
function randomString(length = 8, mixedCase = false) {
    let text = '';
    const possible = mixedCase
        ? 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
        : 'abcdefghijklmnopqrstuvwxyz0123456789';
    for (let i = 0; i < length; i += 1) {
        text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
}
const reOne = /([a-z])([A-Z])([A-Z])/g;
const reTwo = /([a-z])([A-Z])/g;
const reThree = /([A-Z])([A-Z])([a-z])/g;
const camelToNormal = (str, separator = ' ') => {
    if (isNotDefined(str)) {
        return str;
    }
    return str.replace(reOne, (_, one, two, three) => `${one}${separator}${two}${three}`)
        .replace(reTwo, (_, one, two) => `${one}${separator}${two.toLowerCase()}`)
        .replace(reThree, (_, one, two, three) => `${one}${separator}${two.toLowerCase()}${three}`);
};
const camelToSnake = (str) => {
    const value = camelToNormal(str, '_');
    if (isNotDefined(value) || value.length <= 1) {
        return value;
    }
    return value.charAt(0).toLowerCase() + value.slice(1);
};
function camelToKebab(str) {
    const value = camelToNormal(str, '-');
    if (isNotDefined(value) || value.length <= 1) {
        return value;
    }
    return value.charAt(0).toLowerCase() + value.slice(1);
}
function splitInWhitespace(str) {
    if (isNotDefined(str)) {
        return [];
    }
    return str.match(/\S+/g) || [];
}
function trimWhitespace(str) {
    if (isNotDefined(str)) {
        return str;
    }
    return splitInWhitespace(str).join(' ');
}
function caseInsensitiveSubmatch(longText, shortText) {
    if (isNotDefined(longText) || isNotDefined(shortText)) {
        return false;
    }
    return (String(longText)
        .trim()
        .toLowerCase()).includes(String(shortText)
        .trim()
        .toLowerCase());
}

const MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const DAYS = [
    'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat',
];
var ValueType;
(function (ValueType) {
    ValueType["time"] = "time";
    ValueType["date"] = "date";
})(ValueType || (ValueType = {}));
function getStartAndEnd(format, matches) {
    let minStart;
    let maxEnd;
    matches.forEach((val) => {
        const start = format.indexOf(val);
        if (start === -1) {
            return;
        }
        const end = start + val.length;
        if (minStart === undefined || start < minStart) {
            minStart = start;
        }
        if (maxEnd === undefined || end > maxEnd) {
            maxEnd = end;
        }
    });
    return {
        start: minStart,
        end: maxEnd,
    };
}
function breakFormat(format) {
    const { start: dateStart, end: dateEnd, } = getStartAndEnd(format, ['yyyy', 'yy', 'MMM', 'MM', 'dd', 'EEE']);
    const { start: timeStart, end: timeEnd, } = getStartAndEnd(format, ['hh', 'mm', 'ss', 'aaa']);
    const formatEnd = format.length;
    const getSubFormat = (start, end) => {
        if (start === undefined || end === undefined) {
            return '';
        }
        return format.substring(start, end);
    };
    if (dateStart === undefined && timeStart === undefined) {
        return [
            { value: format },
        ].filter(a => a.value !== '');
    }
    else if (dateStart === undefined) {
        return [
            { value: getSubFormat(0, timeStart) },
            {
                type: ValueType.time,
                value: getSubFormat(timeStart, timeEnd),
            },
            { value: getSubFormat(timeEnd, formatEnd) },
        ].filter(a => a.value !== '');
    }
    else if (timeStart === undefined) {
        return [
            { value: getSubFormat(0, dateStart) },
            {
                type: ValueType.date,
                value: getSubFormat(dateStart, dateEnd),
            },
            { value: getSubFormat(dateEnd, formatEnd) },
        ].filter(a => a.value !== '');
    }
    else if (dateStart < timeStart) {
        return [
            { value: getSubFormat(0, dateStart) },
            {
                type: ValueType.date,
                value: getSubFormat(dateStart, dateEnd),
            },
            { value: getSubFormat(dateEnd, timeStart) },
            {
                type: ValueType.time,
                value: getSubFormat(timeStart, timeEnd),
            },
            { value: getSubFormat(timeEnd, formatEnd) },
        ].filter(a => a.value !== '');
    }
    return [
        { value: getSubFormat(0, timeStart) },
        {
            type: ValueType.time,
            value: getSubFormat(timeStart, timeEnd),
        },
        { value: getSubFormat(timeEnd, dateStart) },
        {
            type: ValueType.date,
            value: getSubFormat(dateStart, dateEnd),
        },
        { value: getSubFormat(dateEnd, formatEnd) },
    ].filter(a => a.value !== '');
}
function populateFormat(formatList, date) {
    return formatList.map((format) => {
        if (format.type === ValueType.date) {
            const year = date.getFullYear();
            const month = date.getMonth() + 1;
            const day = date.getDate();
            const weekName = DAYS[date.getDay()];
            const newFormat = { ...format };
            newFormat.value = newFormat.value
                .replace('yyyy', String(year))
                .replace('yy', String(date.getFullYear() % 100))
                .replace('MMM', MONTHS[date.getMonth()])
                .replace('MM', padStart(month, 2))
                .replace('EEE', weekName)
                .replace('dd', padStart(day, 2));
            return newFormat;
        }
        else if (format.type === ValueType.time) {
            const ttIndex = format.value.indexOf('aaa');
            const originalHour = date.getHours();
            const hour = ttIndex >= 0
                ? ((originalHour - 1) % 12) + 1
                : originalHour;
            const amPm = originalHour >= 12 ? 'PM' : 'AM';
            const minute = date.getMinutes();
            const second = date.getSeconds();
            const newFormat = { ...format };
            newFormat.value = newFormat.value
                .replace('hh', padStart(hour, 2))
                .replace('mm', padStart(minute, 2))
                .replace('ss', padStart(second, 2))
                .replace('aaa', amPm);
            return newFormat;
        }
        return format;
    });
}
function formatDateToString(date, format) {
    return populateFormat(breakFormat(format), date)
        .map(e => e.value)
        .join('');
}
function getDate(datetime) {
    const today = new Date(datetime);
    today.setHours(0, 0, 0, 0);
    return today.getTime();
}
function getDifferenceInDays(a, b) {
    const dateA = getDate(a);
    const dateB = getDate(b);
    return (dateA - dateB) / (1000 * 60 * 60 * 24);
}
function getDateDifferenceHumanReadable(a, b) {
    const diff = getDifferenceInDays(a, b);
    if (diff === 0) {
        return 'Today';
    }
    if (diff === -1) {
        return 'Yesterday';
    }
    if (diff === 1) {
        return 'Tomorrow';
    }
    if (diff < 0) {
        return `${-diff} days ago`;
    }
    return `After ${diff} days`;
}
function getNumDaysInMonthX(year, month) {
    if (isTruthy(year) && isTruthy(month)) {
        return new Date(year, month, 0).getDate();
    }
    return 32;
}
function getNumDaysInMonth(date) {
    if (date) {
        return getNumDaysInMonthX(date.getFullYear(), date.getMonth() + 1);
    }
    return 32;
}
function encodeDate(date) {
    return formatDateToString(date, 'yyyy-MM-dd');
}
function decodeDate(value) {
    if (!value) {
        return undefined;
    }
    if (typeof value === 'number' || value.indexOf('T') >= 0) {
        return new Date(value);
    }
    const splits = value.split('-');
    return new Date(Number(splits[0]), Number(splits[1]) - 1, Number(splits[2]));
}
const MIN_YEAR = 1990;
function isDateValuesComplete(ymd) {
    const { yearValue, monthValue, dayValue } = ymd;
    return ((isTruthy(dayValue) && isTruthy(monthValue) && isTruthy(yearValue)) ||
        (isFalsy(dayValue) && isFalsy(monthValue) && isFalsy(yearValue)));
}
function getErrorForDateValues(ymd) {
    if (!isDateValuesComplete(ymd)) {
        return 'Date values incomplete';
    }
    const { yearValue, monthValue, dayValue } = ymd;
    if (yearValue < MIN_YEAR) {
        return `Year should be greater than or equal to ${MIN_YEAR}`;
    }
    if (monthValue < 1 || monthValue > 12) {
        return 'Month should be between 1 and 12';
    }
    const maxNumberOfDays = getNumDaysInMonthX(yearValue, monthValue);
    if (dayValue < 1 || dayValue > maxNumberOfDays) {
        return 'Invalid day for this month';
    }
    return undefined;
}
const MIN_HOUR = 0;
const MAX_HOUR = 23;
const MIN_MINUTE = 0;
const MAX_MINUTE = 59;
const MIN_SECOND = 0;
const MAX_SECOND = 59;
const isTimeValuesComplete = (val) => {
    const { hourValue, minuteValue, secondValue } = val;
    return ((isTruthy(hourValue) && isTruthy(minuteValue) && isTruthy(secondValue)) ||
        (isFalsy(hourValue) && isFalsy(minuteValue) && isFalsy(secondValue)));
};
const getErrorForTimeValues = (val) => {
    if (!isTimeValuesComplete(val)) {
        return 'Time values incomplete';
    }
    const { hourValue, minuteValue, secondValue } = val;
    if (hourValue < MIN_HOUR || hourValue > MAX_HOUR) {
        return `Hour must be between ${MIN_HOUR} and ${MAX_HOUR}`;
    }
    else if (minuteValue < MIN_MINUTE || minuteValue > MAX_MINUTE) {
        return `Minute must be between ${MIN_MINUTE} and ${MAX_MINUTE}`;
    }
    else if (secondValue < MIN_SECOND || secondValue > MAX_SECOND) {
        return `Second must be between ${MIN_SECOND} and ${MAX_SECOND}`;
    }
    return undefined;
};

function getLinkedListNode(node, n, selector) {
    let newNode = node;
    for (let i = 0; i < n; i += 1) {
        newNode = selector(newNode, i);
        if (isNotDefined(newNode)) {
            break;
        }
    }
    return newNode;
}

function listToMap(list, keySelector, modifier) {
    if (isNotDefined(list)) {
        return {};
    }
    return list.reduce((acc, elem, i) => {
        const key = keySelector(elem);
        acc[key] = modifier
            ? modifier(elem, key, i, acc)
            : elem;
        return acc;
    }, {});
}
function mapToList(obj, modifier) {
    if (isNotDefined(obj)) {
        return [];
    }
    return Object.keys(obj).reduce((acc, key, i) => {
        const elem = obj[key];
        acc.push(modifier ? modifier(elem, key, i, acc) : elem);
        return acc;
    }, []);
}
function mapToMap(obj, keySelector, modifier) {
    if (isNotDefined(obj)) {
        return {};
    }
    return Object.keys(obj).reduce((acc, k, i) => {
        const elem = obj[k];
        const key = keySelector ? keySelector(k, elem) : k;
        acc[key] = modifier ? modifier(elem, k, i, acc) : elem;
        return acc;
    }, {});
}
function listToGroupList(list, keySelector, modifier) {
    if (isNotDefined(list)) {
        return {};
    }
    return list.reduce((acc, elem, i) => {
        const key = keySelector(elem);
        const value = modifier
            ? modifier(elem, key, i, acc)
            : elem;
        if (acc[key]) {
            acc[key].push(value);
        }
        else {
            acc[key] = [value];
        }
        return acc;
    }, {});
}

function isListEqual(list1, list2) {
    return (list1.length === list2.length &&
        list1.every((d, i) => d === list2[i]));
}
function getRandomFromList(items) {
    return items[Math.floor(Math.random() * items.length)];
}
function getElementAround(list, index) {
    if (index + 1 < list.length) {
        return list[index + 1];
    }
    else if (index - 1 >= 0) {
        return list[index - 1];
    }
    return undefined;
}
function getDefinedElementAround(list, currentIndex) {
    let i;
    let j;
    for (i = currentIndex - 1; i >= 0; i -= 1) {
        if (list[i] !== undefined) {
            break;
        }
    }
    for (j = currentIndex + 1; j < list.length; j += 1) {
        if (list[j] !== undefined) {
            break;
        }
    }
    if (i < 0 && j >= list.length) {
        return undefined;
    }
    else if (i < 0 && j < list.length) {
        return list[j];
    }
    else if (j >= list.length && i >= 0) {
        return list[i];
    }
    const iDist = currentIndex - i;
    const jDist = j - currentIndex;
    if (jDist > iDist) {
        return list[i];
    }
    return list[j];
}
function getDuplicates(list, keySelector) {
    const counts = listToMap(list, keySelector, (_, key, __, acc) => (isDefined(acc[key]) ? acc[key] + 1 : 1));
    return Object.keys(counts).filter(key => counts[key] > 1);
}
function findDifferenceInList(listA, listB, keySelector) {
    const modified = [];
    const added = [];
    const removed = [];
    const unmodified = [];
    const mapA = listToMap(listA, keySelector, e => e);
    listB.forEach((elem) => {
        const key = keySelector(elem);
        if (isNotDefined(mapA[key])) {
            added.push(elem);
        }
        else if (mapA[key] !== elem) {
            modified.push({ old: mapA[key], new: elem });
        }
        else {
            unmodified.push(elem);
        }
    });
    const mapB = listToMap(listB, keySelector, e => e);
    listA.forEach((elem) => {
        const key = keySelector(elem);
        if (isNotDefined(mapB[key])) {
            removed.push(elem);
        }
    });
    return {
        added,
        modified,
        removed,
        unmodified,
    };
}
function unique(list, getItemHash) {
    if (isNotDefined(list)) {
        return undefined;
    }
    const memory = {};
    const arrWithUnique = [];
    list.forEach((item) => {
        const hash = getItemHash
            ? getItemHash(item)
            : JSON.stringify(item);
        if (!memory[hash]) {
            memory[hash] = true;
            arrWithUnique.push(item);
        }
    });
    if (list.length === arrWithUnique.length) {
        return list;
    }
    return arrWithUnique;
}

function checkVersion(oldVersionId, newVersionId) {
    return {
        shouldSetValue: isFalsy(oldVersionId) || oldVersionId < newVersionId,
        isValueOverriden: isTruthy(oldVersionId) && oldVersionId < newVersionId,
    };
}

function addSeparator(num, separator = ',', decimalSeparator = '.') {
    if (isNotDefined(num)) {
        return num;
    }
    const [before, after] = String(num).split(decimalSeparator);
    let x1 = before;
    const rgx = /(\d+)(\d{3})/;
    while (rgx.test(x1)) {
        x1 = x1.replace(rgx, `$1${separator}$2`);
    }
    const x2 = after !== undefined ? `${decimalSeparator}${after}` : '';
    return x1 + x2;
}
var Lang;
(function (Lang) {
    Lang["en"] = "en";
    Lang["np"] = "ne";
})(Lang || (Lang = {}));
const nepaliMapping = [
    { suffix: 'Ar', value: 1000000000 },
    { suffix: 'Cr', value: 10000000 },
    { suffix: 'Lac', value: 100000 },
];
const englishMapping = [
    { suffix: 'B', value: 1000000000 },
    { suffix: 'M', value: 1000000 },
    { suffix: 'K', value: 1000 },
];
const mappings = {
    [Lang.np]: nepaliMapping,
    [Lang.en]: englishMapping,
};
function formattedNormalize(num, lang = Lang.np) {
    const mapping = mappings[lang];
    if (isNotDefined(mapping)) {
        console.error(`Mapping not defined for ${lang}`);
        return { number: num };
    }
    const rule = mapping.find(n => num >= n.value);
    if (isNotDefined(rule)) {
        return { number: num };
    }
    const { suffix, value } = rule;
    return {
        number: num / value,
        normalizeSuffix: suffix,
    };
}

function isList(item) {
    return Array.isArray(item);
}
function isObject(item) {
    return item !== null && typeof item === 'object' && !isList(item);
}
function isInteger(value) {
    return typeof value === 'number' && value % 1 === 0;
}
const basicTypes = [
    'Boolean',
    'Number',
    'String',
    'Function',
    'Array',
    'Date',
    'RegExp',
    'Object',
    'Error',
    'Symbol',
];
const classToType = basicTypes.reduce((acc, type) => ({
    ...acc,
    [`[object ${type}]`]: type.toLowerCase(),
}), {});
const typeOf = (item) => {
    if (item == null) {
        return 'null';
    }
    const simpleType = typeof item;
    return (simpleType === 'object' || simpleType === 'function')
        ? (classToType[classToType.toString.call(item)] || 'object')
        : simpleType;
};
function isValidEmail(value) {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(value);
}
function isValidUrl(value) {
    const re = new RegExp('^' +
        '(?:(?:https?|ftp)://)' +
        '(?:\\S+(?::\\S*)?@)?' +
        '(?:' +
        '(?!(?:10|127)(?:\\.\\d{1,3}){3})' +
        '(?!(?:169\\.254|192\\.168)(?:\\.\\d{1,3}){2})' +
        '(?!172\\.(?:1[6-9]|2\\d|3[0-1])(?:\\.\\d{1,3}){2})' +
        '(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])' +
        '(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}' +
        '(?:\\.(?:[1-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))' +
        '|' +
        '(?:(?:[_a-z\\u00a1-\\uffff0-9]-*)*[a-z\\u00a1-\\uffff0-9]+)' +
        '(?:\\.(?:[a-z\\u00a1-\\uffff0-9]-*)*[a-z\\u00a1-\\uffff0-9]+)*' +
        '(?:\\.(?:[a-z\\u00a1-\\uffff]{2,}))' +
        '\\.?' +
        ')' +
        '(?::\\d{2,5})?' +
        '(?:[/?#]\\S*)?' +
        '$', 'i');
    return re.test(value);
}

const pick = (obj, keys) => keys.reduce((acc, key) => ({ ...acc, [key]: obj[key] }), {});
function removeKey(obj, key) {
    if (isNotDefined(obj) || !(key in obj)) {
        return obj;
    }
    const newObj = { ...obj };
    delete newObj[key];
    return newObj;
}
function getFirstKeyByValue(obj, value) {
    return Object.keys(obj).find(key => obj[key] === value);
}
function doesObjectHaveNoData(obj, invalids = []) {
    if (obj === undefined) {
        return true;
    }
    if (isList(obj)) {
        if (obj.length <= 0) {
            return true;
        }
        return obj.every(e => doesObjectHaveNoData(e, invalids));
    }
    if (isObject(obj)) {
        if (Object.keys(obj).length <= 0) {
            return true;
        }
        return Object.keys(obj).every(key => doesObjectHaveNoData(obj[key], invalids));
    }
    return invalids.includes(obj);
}

function reverseRoute(route, params) {
    const paths = route.split('/');
    for (let i = 0; i < paths.length; i += 1) {
        let path = paths[i];
        if (path && path.length > 0 && path.charAt(0) === ':') {
            path = path.substring(1);
            let param;
            if (path.slice(-1) === '?') {
                param = params[path.replace('?', '')];
                if (!param) {
                    paths.splice(i, 1);
                }
                else {
                    paths[i] = String(param);
                }
            }
            else {
                param = params[path];
                if (!param) {
                    console.error(`Value for param ${path} not supplied`);
                }
                paths[i] = String(param);
            }
        }
    }
    return paths.join('/');
}
function isParamRequired(route, param) {
    const re = new RegExp(`:${param}([^\\?]|$)`);
    return route.search(re) !== -1;
}

function modulo(a, b) {
    const value = a % b;
    if (value < 0) {
        return b + value;
    }
    return Math.abs(value);
}
function sum(values) {
    return values.reduce((acc, b) => acc + b, 0);
}
function mean(values) {
    if (values.length <= 0) {
        return 0;
    }
    return sum(values) / values.length;
}
function median(values) {
    if (values.length <= 0) {
        return undefined;
    }
    const sortedValues = [...values].sort();
    const centerIndex = Math.floor(sortedValues.length / 2);
    return sortedValues.length % 2
        ? sortedValues[centerIndex]
        : (sortedValues[centerIndex - 1] + sortedValues[centerIndex]) / 2.0;
}
function bucket(value, buckets) {
    const currentBucket = buckets.find(([min, max]) => (value >= min && value < max));
    return currentBucket && currentBucket[2];
}
function bound(value, a, b) {
    const min = Math.min(a, b);
    const max = Math.max(a, b);
    return Math.max(min, Math.min(max, value));
}
function normalize(value, max, min) {
    return (value - min) / (max - min);
}
function getNumbers(start, end) {
    const list = [];
    for (let i = start; i < end; i += 1) {
        list.push(i);
    }
    return list;
}

function getTrigrams(sentence) {
    const newWord = sentence.trim()
        .toLowerCase()
        .replace(/[^a-zA-z0-9]/g, ' ')
        .replace(/\s+/g, '  ');
    const formattedWord = `  ${newWord} `;
    const trigrams = [];
    for (let i = 0; i < formattedWord.length - 2; i += 1) {
        const trigram = formattedWord.substr(i, 3);
        if (trigram[1] !== ' ' || trigram[2] !== ' ') {
            trigrams.push(trigram);
        }
    }
    return new Set(trigrams);
}
function getTrigramSimilarity(foo, bar) {
    const fooTrigrams = getTrigrams(foo);
    const barTrigrams = getTrigrams(bar);
    const common = intersection(fooTrigrams, barTrigrams);
    const all = union(fooTrigrams, barTrigrams);
    return common.size / all.size;
}

export { Lang, MIN_YEAR, _cs, addSeparator, basicTypes, bound, breakFormat, bucket, camelToKebab, camelToNormal, camelToSnake, capitalize, caseInsensitiveSubmatch, checkVersion, compareBoolean, compareDate, compareLength, compareNumber, compareString, compareStringAsNumber, compareStringByWordCount, compareStringSearch, decodeDate, difference, doesObjectHaveNoData, encodeDate, findDifferenceInList, formatDateToString, formatPdfText, formattedNormalize, getColorOnBgColor, getContrastYIQ, getDate, getDateDifferenceHumanReadable, getDefinedElementAround, getDifferenceInDays, getDuplicates, getElementAround, getErrorForDateValues, getErrorForTimeValues, getFirstKeyByValue, getHashFromString, getHexFromCode, getHexFromRgb, getHexFromRgbRaw, getHexFromString, getLinkedListNode, getNumDaysInMonth, getNumDaysInMonthX, getNumbers, getRandomFromList, getRatingForContentInString, getRgbFromHex, getRgbRawFromHex, getTrigramSimilarity, getTrigrams, interpolateRgb, intersection, isDateValuesComplete, isDefined, isFalsy, isFalsyString, isInteger, isList, isListEqual, isNaN, isNotDefined, isObject, isParamRequired, isTimeValuesComplete, isTruthy, isTruthyString, isValidEmail, isValidHexColor, isValidUrl, listToGroupList, listToMap, mapToList, mapToMap, mean, median, modulo, noOp, normalize, padStart, pick, populateFormat, randomString, removeKey, resolve, reverseRoute, splitInWhitespace, sum, trimWhitespace, typeOf, union, unique };
